// ── 미팅맵 메인 컴포넌트 ─────────────────────────────────────────────────────
// 상태: hooks/useMeetingMapCore
// 지도: hooks/useLeafletMap
// API + 이펙트: hooks/useMeetingMapHandlers
// 결과 입력: hooks/useMeetingMapActions
// 서브컴포넌트: components/ 폴더

import React, { useEffect, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon   from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useToast } from '../../components/Toast';
import { apiClient } from '../../api/apiClient';
import { TXT, addDaysYmd } from '../../components/meetingmap/utils';
import {
  wrapStyle,
  dateNavRowStyle, dateArrowBtnStyle, dateArrowIconStyle,
  dateCenterWrapStyle, dateMainLabelStyle, datePickerInputStyle, dateMetaRowStyle,
  dateTodayBadgeStyle, dateWeekdayStyle,
  mapWrapStyle, mapGuideStyle,
} from '../../components/meetingmap/styles';

// ── 훅 ───────────────────────────────────────────────────────────────────────
import { useMeetingMapCore }     from './hooks/useMeetingMapCore';
import { HQ_MEETING_API }        from './meetingMapApi';
import { useLeafletMap }         from './hooks/useLeafletMap';
import { useMeetingMapHandlers } from './hooks/useMeetingMapHandlers';
import { useMeetingMapActions }  from './hooks/useMeetingMapActions';

// ── 서브컴포넌트 ─────────────────────────────────────────────────────────────
import { MeetingSheet }          from './components/MeetingSheet';
import { MeetingListView }       from './components/MeetingListView';
import { MeetingMapHeader }      from './components/MeetingMapHeader';
import { useListTextSize }       from './components/useListTextSize';
import { PendingFeedbackModal }  from './components/PendingFeedbackModal';
import { KeepListModal }         from './components/KeepListModal';
import { ResultModal }           from './components/ResultModal';
import { ContractConfirmModal }  from './components/ContractConfirmModal';

// Leaflet 기본 마커 이미지 경로 수동 지정 (webpack 빌드 환경 대응)
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl:       markerIcon,
  shadowUrl:     markerShadow,
});

// refreshToken: 관리자 ⚙️ 설정 모달을 닫을 때 증가 — 배너·피드백 옵션·인계 담당자를
//   새로고침 없이 재조회한다 (2026-09-02). 직원용(/my/meeting-map)은 미전달 = 0 고정, 기존 동작 동일.
// api: 관리자 주소 묶음(2026-10-22) — 본사 = HQ_MEETING_API(예전 그대로), 회사별·통합 화면이 자기 묶음을 넘긴다.
export default function MeetingMap({ adminMode = false, adminSalesName = '', refreshToken = 0, api = HQ_MEETING_API }) {
  const { showToast } = useToast();

  // ── 배너 문구 (미팅지도 관리 ⚙️ 설정에서 변경) ───────────────────────────────
  const [bannerPrefix, setBannerPrefix] = useState('오늘 미팅은');
  const [bannerSuffix, setBannerSuffix] = useState('오늘도 화이팅! 💪');
  useEffect(() => {
    apiClient.get('/api/tm/meeting-banner').then((res) => {
      if (res.data?.success) {
        setBannerPrefix(res.data.prefix ?? '오늘 미팅은');
        setBannerSuffix(res.data.suffix ?? '오늘도 화이팅! 💪');
      }
    }).catch(() => {});
  }, [refreshToken]);

  // ── 상태 · 파생 상태 ──────────────────────────────────────────────────────
  const core = useMeetingMapCore();

  // ── 이펙트 + API 함수 ─────────────────────────────────────────────────────
  const handlers = useMeetingMapHandlers(core, { adminMode, adminSalesName, showToast, refreshToken, api });

  // ── 결과 입력 액션 ────────────────────────────────────────────────────────
  const actions = useMeetingMapActions(core, {
    adminMode,
    api,
    showToast,
    fetchMeetings:            handlers.fetchMeetings,
    fetchPendingFeedback:     handlers.fetchPendingFeedback,
    fetchMeetingRefs:         handlers.fetchMeetingRefs,
    fetchContractTicketTypes: handlers.fetchContractTicketTypes,
    extractValidity:          handlers.extractValidity,
    parseContractTicketString:handlers.parseContractTicketString,
  });

  // ── Leaflet 지도 ──────────────────────────────────────────────────────────
  useLeafletMap(core);

  // ── 구조분해 (JSX 가독성) ─────────────────────────────────────────────────
  const {
    isMobile, view, setView, visitDate, setVisitDate, loading,
    sheetOpen, setSheetOpen, selected,
    pendingList, pendingOpen, setPendingOpen, pendingLoading,
    isTodaySelected, centerDateLabel, centerWeekdayLabel,
    sheetDragY, sheetHandleRef, sheetRef, sheetDragging,
    resultOpen, setResultOpen, resultForm, setResultForm,
    sortedFeedbackOptions, salesPersons, contractTicketTypes,
    savingResult, originalIsKeep,
    keepListOpen, setKeepListOpen, keepList, setKeepList,
    keepListLoading, setMeetings,
    confirmContractOpen, setConfirmContractOpen,
    mapContainerRef, listWrapRef, meetings,
    SHEET_DISMISS_THRESHOLD,
  } = core;

  const { openResultModal, saveResult } = actions;

  // 목록 글자 크게 보기 (2026-09-11) — 계정에 기억, 목록 탭에서만 쓴다
  const { textSize, toggleTextSize } = useListTextSize();

  const {
    fetchPendingFeedback, fetchContractTicketTypes, hasValiditySetting,
    resetMarkerLayout,
  } = handlers;

  // ── 렌더 ─────────────────────────────────────────────────────────────────
  // 당겨서-새로고침 제거(지도 제스처와 충돌) → 헤더 🔄 버튼으로 대체 (배치 유지 재조회)
  return (
    <section style={wrapStyle}>
      <MeetingMapHeader
        view={view}
        setView={setView}
        pendingList={pendingList}
        onPendingOpen={() => { setPendingOpen(true); fetchPendingFeedback(); }}
        onResetMarkerLayout={() => {
          if (window.confirm('이 날짜의 마커 배치를 원래 위치로 되돌릴까요?')) resetMarkerLayout();
        }}
        textSize={textSize}
        onToggleTextSize={toggleTextSize}
      />

      {/* ── 날짜 네비게이션 ────────────────────────────────────── */}
      <div style={dateNavRowStyle}>
        <button type="button" style={dateArrowBtnStyle}
          onClick={() => setVisitDate((prev) => addDaysYmd(prev, -1))} aria-label="이전 날짜">
          <span style={dateArrowIconStyle}>‹</span>
        </button>
        <div style={dateCenterWrapStyle}>
          {/* 날짜를 눌러 달력으로 바로 이동 (2026-09-09).
              화살표만 있어서 지난 미팅을 보려면 하루씩 여러 번 눌러야 했다.
              결과 입력창 [미뤄짐]의 날짜 칸과 같은 방식이다. */}
          <label style={dateMainLabelStyle}>
            {centerDateLabel}
            <input
              type="date"
              value={visitDate}
              onChange={(e) => { if (e.target.value) setVisitDate(e.target.value); }}
              style={datePickerInputStyle}
              aria-label="날짜 선택"
            />
          </label>
          <div style={dateMetaRowStyle}>
            {isTodaySelected && <span style={dateTodayBadgeStyle}>오늘</span>}
            <span style={dateWeekdayStyle}>{centerWeekdayLabel}</span>
            {/* 건수는 데이터가 도착했다는 가장 확실한 신호다 — 날짜와 무관하게 항상 보인다 */}
            {!loading && <span style={dateWeekdayStyle}>· 미팅 {meetings.length}건</span>}
          </div>
        </div>
        <button type="button" style={dateArrowBtnStyle}
          onClick={() => setVisitDate((prev) => addDaysYmd(prev, 1))} aria-label="다음 날짜">
          <span style={dateArrowIconStyle}>›</span>
        </button>
      </div>

      {/* ── 지도 뷰 ───────────────────────────────────────────── */}
      <div style={{ ...mapWrapStyle(isMobile), display: view === 'map' ? 'block' : 'none' }}>
        <div ref={mapContainerRef} style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
        {/* 로딩 중: 로딩 문구 / 오늘+미팅 있음: 배너 문구 / 그 외: 마커 안내 */}
        <div style={mapGuideStyle}>
          {loading
            ? TXT.loading
            : (isTodaySelected && meetings.length > 0)
              ? <><strong>{bannerPrefix}</strong> {meetings.length}개입니다. {bannerSuffix}</>
              : TXT.markerGuide
          }
        </div>
        {!loading && sheetOpen && selected && (
          <MeetingSheet
            item={selected}
            isMobile={isMobile}
            sheetDragY={sheetDragY}
            sheetDragging={sheetDragging}
            sheetHandleRef={sheetHandleRef}
            sheetRef={sheetRef}
            SHEET_DISMISS_THRESHOLD={SHEET_DISMISS_THRESHOLD}
            onClose={() => setSheetOpen(false)}
            onResultOpen={openResultModal}
            showToast={showToast}
          />
        )}
      </div>

      {/* ── 목록 뷰 ───────────────────────────────────────────── */}
      <MeetingListView
        loading={loading}
        meetings={meetings}
        listWrapRef={listWrapRef}
        view={view}
        onResultOpen={openResultModal}
        showToast={showToast}
        textSize={textSize}
      />

      {/* ── 피드백 미입력 모달 ─────────────────────────────────── */}
      <PendingFeedbackModal
        open={pendingOpen}
        onClose={() => setPendingOpen(false)}
        pendingLoading={pendingLoading}
        pendingList={pendingList}
        adminMode={adminMode}
        onEntryClick={(item) => {
          // pending 모달 닫고 해당 날짜로 이동 후 결과 입력 모달 열기
          setPendingOpen(false);
          setVisitDate(item.visit_date);
          openResultModal(item);
        }}
      />

      {/* ── 킵목록 모달 ───────────────────────────────────────── */}
      <KeepListModal
        open={keepListOpen}
        onClose={() => setKeepListOpen(false)}
        keepListLoading={keepListLoading}
        keepList={keepList}
        setKeepList={setKeepList}
        setMeetings={setMeetings}
        adminMode={adminMode}
        api={api}
        showToast={showToast}
      />

      {/* ── 결과 입력 모달 ─────────────────────────────────────── */}
      <ResultModal
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        onSave={saveResult}
        resultForm={resultForm}
        setResultForm={setResultForm}
        sortedFeedbackOptions={sortedFeedbackOptions}
        salesPersons={salesPersons}
        contractTicketTypes={contractTicketTypes}
        savingResult={savingResult}
        originalIsKeep={originalIsKeep}
        adminMode={adminMode}
        fetchContractTicketTypes={fetchContractTicketTypes}
        hasValiditySetting={hasValiditySetting}
        onConfirmContract={() => setConfirmContractOpen(true)}
      />

      {/* ── 계약 접수 확인 모달 ────────────────────────────────── */}
      <ContractConfirmModal
        open={confirmContractOpen}
        onClose={() => setConfirmContractOpen(false)}
        onConfirm={() => { setConfirmContractOpen(false); saveResult(); }}
        savingResult={savingResult}
      />
    </section>
  );
}
